import type { ReactNode } from 'react';
import { formatMoney } from '@/utils/format';
import type {
  ComplianceState,
  DriverStatus,
  IncidentSeverity,
  IncidentStatus,
  MaintenanceStatus,
  RouteStop,
  TrackingFreshness,
  TransportAttendanceStatus,
  TransportEnrollmentStatus,
  TransportStatus,
  TripStatus,
  VehicleStatus,
} from '@/types/transport';

/*
 * Pieces every transport screen uses.
 *
 * Kept together for the same reason the library has a shared file: sixteen
 * screens cannot be allowed to grow sixteen slightly different ways of
 * colouring a status. The status maps are exhaustive over their union types on
 * purpose — adding a state on the server becomes a compile error here rather
 * than a blank badge nobody notices.
 */

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'border-line bg-surface text-muted',
  success: 'border-[color:var(--sms-success)] text-[color:var(--sms-success)]',
  warning: 'border-[color:var(--sms-warning)] text-[color:var(--sms-warning)]',
  danger: 'border-[color:var(--sms-danger)] text-[color:var(--sms-danger)]',
  info: 'border-accent text-accent',
};

/** A small status pill. Text as well as colour: colour alone is not a label. */
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.03em] ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * A vehicle's state. UNDER_MAINTENANCE is a warning, not a danger: the metal
 * is being cared for, which is what should be happening — it just cannot be
 * on a trip while it is.
 */
const VEHICLE_TONE: Record<VehicleStatus, Tone> = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  UNDER_MAINTENANCE: 'warning',
  OUT_OF_SERVICE: 'danger',
  RETIRED: 'neutral',
};

export function VehicleStatusBadge({ status }: { status: VehicleStatus }) {
  return <Badge tone={VEHICLE_TONE[status]}>{humanise(status)}</Badge>;
}

const DRIVER_TONE: Record<DriverStatus, Tone> = {
  ACTIVE: 'success',
  INACTIVE: 'neutral',
  SUSPENDED: 'danger',
  ON_LEAVE: 'warning',
};

export function DriverStatusBadge({ status }: { status: DriverStatus }) {
  return <Badge tone={DRIVER_TONE[status]}>{humanise(status)}</Badge>;
}

/** Routes and stops: on or off, nothing in between. */
export function RouteStatusBadge({ status }: { status: TransportStatus }) {
  return <Badge tone={status === 'ACTIVE' ? 'success' : 'neutral'}>{humanise(status)}</Badge>;
}

/**
 * PENDING reads as info rather than a warning: a seat is held and nothing is
 * billed, which is the intended state of a family mid-paperwork, not a
 * problem.
 */
const ENROLLMENT_TONE: Record<TransportEnrollmentStatus, Tone> = {
  PENDING: 'info',
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  CANCELLED: 'neutral',
  COMPLETED: 'neutral',
};

export function EnrollmentStatusBadge({ status }: { status: TransportEnrollmentStatus }) {
  return <Badge tone={ENROLLMENT_TONE[status]}>{humanise(status)}</Badge>;
}

/**
 * A trip's state. CANCELLED is a danger rather than a neutral: a bus that did
 * not run is children who did not ride, and the day board must not let that
 * read as tidy.
 */
const TRIP_TONE: Record<TripStatus, Tone> = {
  SCHEDULED: 'neutral',
  BOARDING: 'info',
  IN_PROGRESS: 'info',
  DELAYED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};

export function TripStatusBadge({ status }: { status: TripStatus }) {
  return <Badge tone={TRIP_TONE[status]}>{humanise(status)}</Badge>;
}

/**
 * A rider's mark. NOT_BOARDED is the danger: the bus expected them and they
 * were not on it, which is the one row somebody phones about. ABSENT means
 * the school already knew.
 */
const ATTENDANCE_TONE: Record<TransportAttendanceStatus, Tone> = {
  BOARDED: 'success',
  NOT_BOARDED: 'danger',
  DROPPED_OFF: 'success',
  ABSENT: 'warning',
  EXCUSED: 'neutral',
};

export function AttendanceStatusBadge({ status }: { status?: TransportAttendanceStatus }) {
  // An unmarked row says so out loud — it is never defaulted to boarded.
  if (status === undefined) {
    return <Badge tone="neutral">unmarked</Badge>;
  }
  return <Badge tone={ATTENDANCE_TONE[status]}>{humanise(status)}</Badge>;
}

const SEVERITY_TONE: Record<IncidentSeverity, Tone> = {
  MINOR: 'neutral',
  MODERATE: 'warning',
  SERIOUS: 'danger',
  CRITICAL: 'danger',
};

export function SeverityBadge({ severity }: { severity: IncidentSeverity }) {
  return <Badge tone={SEVERITY_TONE[severity]}>{humanise(severity)}</Badge>;
}

const INCIDENT_TONE: Record<IncidentStatus, Tone> = {
  REPORTED: 'warning',
  UNDER_REVIEW: 'info',
  ESCALATED: 'danger',
  RESOLVED: 'success',
  CLOSED: 'neutral',
};

export function IncidentStatusBadge({ status }: { status: IncidentStatus }) {
  return <Badge tone={INCIDENT_TONE[status]}>{humanise(status)}</Badge>;
}

/** IN_PROGRESS is a warning because the vehicle is benched while it runs. */
const MAINTENANCE_TONE: Record<MaintenanceStatus, Tone> = {
  SCHEDULED: 'info',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
};

export function MaintenanceStatusBadge({ status }: { status: MaintenanceStatus }) {
  return <Badge tone={MAINTENANCE_TONE[status]}>{humanise(status)}</Badge>;
}

/**
 * A paper's state, as the server derived it. UNDATED is deliberately visible
 * rather than quietly valid: no expiry on file is itself a finding.
 */
const COMPLIANCE_TONE: Record<ComplianceState, Tone> = {
  VALID: 'success',
  EXPIRING: 'warning',
  EXPIRED: 'danger',
  UNDATED: 'neutral',
};

export function ComplianceBadge({ state }: { state: ComplianceState }) {
  return <Badge tone={COMPLIANCE_TONE[state]}>{humanise(state)}</Badge>;
}

/**
 * How stale a position is — the server's verdict, shown verbatim. §32 says
 * old data is never presented as live, so the badge never softens it.
 */
const FRESHNESS_TONE: Record<TrackingFreshness, Tone> = {
  LIVE: 'success',
  DELAYED: 'info',
  STALE: 'warning',
  UNAVAILABLE: 'neutral',
};

export function FreshnessBadge({ freshness }: { freshness: TrackingFreshness }) {
  return <Badge tone={FRESHNESS_TONE[freshness]}>{humanise(freshness)}</Badge>;
}

export function Money({ value, bold = false }: { value?: number | null; bold?: boolean }) {
  return (
    <span
      className={`tabular-nums ${bold ? 'font-bold text-ink' : 'text-ink'}`}
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {value === null || value === undefined ? '—' : formatMoney(value)}
    </span>
  );
}

/** A headline figure with a label under it. */
export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}) {
  const accent =
    tone === 'danger'
      ? 'var(--sms-danger)'
      : tone === 'warning'
        ? 'var(--sms-warning)'
        : tone === 'success'
          ? 'var(--sms-success)'
          : 'var(--sms-accent)';

  return (
    <div className="border border-line bg-bg p-4">
      <div className="h-[3px] w-8" style={{ background: accent }} aria-hidden="true" />
      <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
        {label}
      </p>
      <p className="mt-1 text-[26px] font-extrabold leading-none text-ink">{value}</p>
      {hint !== undefined && <p className="mt-1.5 text-xs text-muted">{hint}</p>}
    </div>
  );
}

/** A labelled row for a detail panel. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-2 last:border-0">
      <dt className="shrink-0 text-xs uppercase tracking-[0.04em] text-muted">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-ink">{children}</dd>
    </div>
  );
}

/** A horizontal bar for a breakdown, sized against the largest value shown. */
export function BarRow({
  label,
  value,
  max,
  suffix,
}: {
  label: string;
  value: number;
  max: number;
  suffix?: ReactNode;
}) {
  const width = max <= 0 ? 0 : Math.max(2, Math.round((value / max) * 100));
  return (
    <div className="py-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate text-ink">{label}</span>
        <span className="shrink-0 tabular-nums text-muted">{suffix ?? value}</span>
      </div>
      <div className="mt-1 h-1.5 w-full bg-surface">
        <div className="h-full bg-accent" style={{ width: `${width}%` }} aria-hidden="true" />
      </div>
    </div>
  );
}

/**
 * The reasons the server gave for refusing something — a driver's eligibility
 * blockers, a generation run's skipped routes.
 *
 * Rendered verbatim. §38 says a critical rule must not live only in React,
 * and the corollary is that the explanation must not either — restating the
 * server's decision in our own words is how the two come to disagree.
 */
export function Blockers({ reasons, title }: { reasons: string[]; title?: string }) {
  if (reasons.length === 0) {
    return null;
  }
  return (
    <div className="border border-[color:var(--sms-danger)] bg-surface p-3">
      {title !== undefined && (
        <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[color:var(--sms-danger)]">
          {title}
        </p>
      )}
      <ul className="mt-1 space-y-0.5 text-sm text-ink">
        {reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A route's stops as an ordered, numbered sequence — §37 rules out a map
 * library, and a numbered list is what a driver actually reads anyway.
 * `direction` picks which time column matters; omit it to show both.
 */
export function StopSequence({
  stops,
  direction,
}: {
  stops: RouteStop[];
  direction?: 'PICKUP' | 'DROPOFF';
}) {
  if (stops.length === 0) {
    return <p className="py-3 text-sm text-muted">No stops on this route yet.</p>;
  }
  return (
    <ol className="divide-y divide-line">
      {stops.map((stop) => (
        <li key={stop.id} className="flex items-start gap-3 py-2.5">
          <span
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-line bg-surface text-xs font-bold tabular-nums text-ink"
            aria-hidden="true"
          >
            {stop.sequence}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-ink">
              {stop.name}
              {stop.status === 'INACTIVE' && (
                <span className="ml-2 align-middle">
                  <Badge tone="neutral">inactive</Badge>
                </span>
              )}
            </p>
            {stop.address !== undefined && (
              <p className="truncate text-xs text-muted">{stop.address}</p>
            )}
          </div>
          <div className="shrink-0 text-right text-xs tabular-nums text-muted">
            {direction !== 'DROPOFF' && stop.allowsPickup && (
              <p>up {formatTime(stop.pickupTime)}</p>
            )}
            {direction !== 'PICKUP' && stop.allowsDropOff && (
              <p>down {formatTime(stop.dropOffTime)}</p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** UNDER_MAINTENANCE → "under maintenance". */
export function humanise(value: string): string {
  return value.replaceAll('_', ' ').toLowerCase();
}

/** "07:15:00" → "07:15". The seconds are the server's precision, not the reader's. */
export function formatTime(value?: string): string {
  if (value === undefined || value === '') {
    return '—';
  }
  return value.length > 5 ? value.slice(0, 5) : value;
}

/** "45s ago" / "12 min ago" / "3 h ago" — a position's age, as a person says it. */
export function ageText(ageSeconds: number): string {
  if (ageSeconds < 60) {
    return `${ageSeconds}s ago`;
  }
  if (ageSeconds < 3600) {
    return `${Math.floor(ageSeconds / 60)} min ago`;
  }
  return `${Math.floor(ageSeconds / 3600)} h ago`;
}

/** "delayed 15 min" — the phrasing the board actually shows. */
export function delayText(minutes?: number): string {
  return minutes === undefined || minutes <= 0 ? 'delayed' : `delayed ${minutes} min`;
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A date that many days ago, for a report's default range. */
export function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}
